import { MRT_ColumnDef, MRT_Row, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';
import AddIcon from '@mui/icons-material/Add';
import IconTrash from 'src/presentations/components/atom/icons/Trash';
import IconNewEdit from 'src/presentations/components/atom/icons/newedit.svg';
import { Box, IconButton, Tooltip, CircularProgress, useTheme } from '@mui/material';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { UserContext } from 'src/use-cases/user/userDetails/context';
import {
  useCreateShippingService,
  useDeleteShippingService,
  useGetAllServiceProviders,
  useGetListBilledEntity,
  useGetShippingService,
  useUpdateShippingService,
  useGetShippingServiceMetadata,
  ShippingServiceFilters,
} from 'src/use-cases/serviceProvider/useServiceProvider';
import { ContractDto, RouteDto, ZoneDto } from 'src/generated/services/TFinancialApi';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import IconInfo from 'src/assets/images/icons/information-outline-grey.svg';
import { CustomTooltip } from 'src/presentations/components/atom/customtooltip';
import IconArrow from 'src/assets/images/icons/arrow-left_ico.svg';
import { beautifyEnum } from 'src/utils/utility.ts';
import { useEnhancedTable } from 'src/presentations/hooks/useEnhancedTable.ts';
import { useShippingServiceFilters } from 'src/presentations/hooks/useShippingServiceFilters';
import { useTourContext } from 'src/presentations/guided-tours';
import { tFinancialService } from 'src/infra/services/tFinancialService.ts';
import { newFormatDateTimeString } from '../../../../utils/date.ts';
import { TradeLaneZoneSelector } from './TradeLaneZoneSelector';
import { Modality, validModalities } from '../../../../types.ts';
import { TableMain } from '../../molecules/table/TableComponents.tsx';
import { ContractManagerColumnHeader } from './ContractManagerColumnHeader';
import { ConsigneeSelector } from './rateCardCharges/ConsigneeSelector';
import { ShipperSelector } from './rateCardCharges/ShipperSelector';
import { UnifiedTablePagination } from '../../molecules/table/UnifiedTablePagination.tsx';
import Button from '../../atom/button';

const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid transparent',
  width: '120px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

// Column mapping for sorting
const getSortingField = (accessorKey: string): string => {
  // Map table column accessorKeys to API's expected sortingColumn values
  const columnMappings: Record<string, string> = {
    // Basic fields that match directly
    id: 'id',
    code: 'code',
    type: 'type',
    carrier: 'carrier',
    route: 'route',
    incoterm: 'incoterm',

    // Fields that need transformation
    service_provider_code: 'serviceProvider.code',
    shipper_code: 'shipper.code',
    consignee_code: 'consignee.code',
    billing_party_code: 'billingParty.code',
    billed_entity: 'billingParty.code', // Map the actual accessor key used in the table
    dominant_modality: 'dominantModality',
    service_name: 'service',
    total_lead_time: 'totalLeadTime',
    created_on: 'createdOn',
    updated_on: 'updatedOn',
  };

  return columnMappings[accessorKey] || accessorKey;
};

export interface IShippingServiceDTO {
  no?: number;
  id: string;
  service_provider_id: number;
  service_provider_code: string;
  shipper_code: string;
  shipper_name: string;
  consignee_code: string;
  consignee_name: string;
  dominant_modality: string;
  listZone?: ZoneDto[];
  route: string;
  service: string;
  type: string;
  carrier: string;
  incoterm: string;
  total_lead_time: number;
  created_on: string;
  billed_entity: number;
}

const normalizeDashValues = (values: Record<string, any>) => {
  Object.keys(values).forEach((key) => {
    if (values[key] === '-') {
      values[key] = null;
    }
  });
};

const buildShippingServicePayload = (values: Record<string, any>, providers: { id: any; code: string }[]) => ({
  code: null,
  serviceProviderId: providers.find((provider) => provider.code === values.service_provider_code)?.id,
  shipperCode: values.shipper_code || null,
  consigneeCode: values.consignee_code || null,
  billingPartyId: values.billed_entity || null,
  incoterm: values.incoterm || null,
  dominantModality: values.dominant_modality,
  route: values?.route,
  service: values.service || null,
  type: values.type || null,
  carrier: values.carrier || null,
  totalLeadTime: values.total_lead_time || null,
});

export const ContractManagerShippingService = ({
  serviceProvider,
  urlShippingService,
  setModality,
  shippingServiceId,
  setShippingServiceId,
  setContract,
  modality,
}: {
  serviceProvider: string;
  urlShippingService: string;
  setModality: (value: Modality) => void;
  shippingServiceId: string;
  setShippingServiceId: (value: string) => void;
  setContract: (contract: ContractDto) => void;
  modality: Modality;
}) => {
  const { appTour } = useTourContext();
  const tour = appTour.registerTour('rate-card-table');
  const theme = useTheme();
  const navigate = useNavigate();
  const { userDetails } = useContext(UserContext);

  // All hooks must be called before any conditional returns
  const routesRef = useRef<RouteDto[]>([]);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);
  const [searchParams] = useSearchParams();
  const { data: listBilledEntity, isLoading } = useGetListBilledEntity();
  const billedEntities = useMemo<{ label: string; value: number }[]>(
    () =>
      listBilledEntity?.data.map((item) => ({
        label: item.code,
        value: item.id,
      })) || [],
    [listBilledEntity]
  );

  const { data: dataAllProviders } = useGetAllServiceProviders(modality);

  // Check user permissions (needed for table configuration and access control)
  const isAdmin = userDetails?.roles?.includes('ADMIN') ?? false;
  const isRateCardViewer = userDetails?.roles?.includes('RATE_CARD_VIEWER') ?? false;
  const hasPageAccess = isAdmin || isRateCardViewer;

  // Use the new shipping service filters hook
  const { filters, isReady: filtersReady, updateFilter } = useShippingServiceFilters(modality, serviceProvider);

  // Use the enhanced table hook
  const { pagination, sorting, handleSortingChange, onChangePage, onChangePageSize, getTextFieldProps, clearValidationErrors } =
    useEnhancedTable<IShippingServiceDTO>({
      initialSorting: [],
      requiredFields: [],
    });

  // Get service provider details from the codes
  const currentServiceProviders = useMemo(() => {
    if (!dataAllProviders?.data) return [];

    if (!filters?.serviceProviderCodes?.length) {
      return dataAllProviders.data.map((provider) => ({
        id: provider.id,
        code: provider.code,
        name: provider.name,
      }));
    }

    return dataAllProviders.data
      .filter((provider) => filters?.serviceProviderCodes?.includes(provider.code))
      .map((provider) => ({
        id: provider.id,
        code: provider.code,
        name: provider.name,
      }));
  }, [dataAllProviders?.data, filters?.serviceProviderCodes]);

  // Function to fetch routes for a service provider
  const fetchRoutes = async (serviceProviderId: number) => {
    try {
      const response = await tFinancialService.api.getContractRoute({ serviceProviderId });
      return response.data.data || [];
    } catch (error) {
      console.error(`Error fetching routes for service provider ${serviceProviderId}:`, error);
      return [];
    }
  };

  // Effect to fetch routes when service providers change
  useEffect(() => {
    const fetchAllRoutes = async () => {
      if (currentServiceProviders.length === 0) {
        routesRef.current = [];
        return;
      }

      // Fetch routes for all service providers in parallel
      const routePromises = currentServiceProviders.map((provider) => fetchRoutes(Number(provider.id)));

      // Combine all routes and remove duplicates
      const results = await Promise.all(routePromises);
      const allRoutes = results.flat();

      // Remove duplicates based on route name
      const uniqueRoutes = allRoutes.filter((route, index, self) => index === self.findIndex((r) => r.route === route.route));

      routesRef.current = uniqueRoutes;
    };

    fetchAllRoutes();
  }, [currentServiceProviders]);

  // Use the routes from the ref
  const routes = useMemo(() => routesRef.current || [], []);

  // Store total items count to keep it persistent
  const totalItemsRef = useRef<number>(0);

  const filtersAndPagination: ShippingServiceFilters = useMemo(
    () => ({
      ...filters,
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sortBy: sorting.length > 0 ? getSortingField(sorting[0].id) : '',
      sortDirection: sorting.length > 0 && sorting[0].desc ? 'DESC' : 'ASC',
      selectedShippingServiceId: urlShippingService ? [urlShippingService] : [],
    }),
    [filters, pagination, sorting, urlShippingService]
  );

  // Only enable API calls after filters have been initialized from props
  // This prevents duplicate calls when props are being initialized from URL params
  const shouldFetchData = filtersReady;

  // Get metadata for filter options
  const { data: metadataData } = useGetShippingServiceMetadata(filtersAndPagination, {
    enabled: shouldFetchData,
  });

  const [validationErrors] = useState<Record<string, string | undefined>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Call the updated API with filters - React Query will automatically refetch when filters change
  const {
    data,
    status: getShippingServiceStatus,
    isLoading: isLoadingShippingService,
  } = useGetShippingService(filtersAndPagination, {
    enabled: shouldFetchData,
  });

  useEffect(() => {
    if (!isLoadingShippingService && data?.data?.content?.length > 0 && urlShippingService) {
      setShippingServiceId(urlShippingService);
    }
  }, [isLoadingShippingService, data?.data?.content?.length, urlShippingService, setShippingServiceId]);

  // Update total items ref when data changes
  useEffect(() => {
    if (data?.data?.totalElements) {
      totalItemsRef.current = data.data.totalElements;
    }
  }, [data?.data?.totalElements]);

  // Update ref when routes change
  useEffect(() => {
    if (routes.length > 0) {
      routesRef.current = routes;
    }
  }, [routes]);

  // Common style for all edit form fields with responsive breakpoints
  const editFieldStyles = {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      [theme.breakpoints.up(1520)]: {
        fontSize: '14px',
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: '10px',
      [theme.breakpoints.up(1520)]: {
        fontSize: '12px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '12px',
      [theme.breakpoints.up(1520)]: {
        fontSize: '14px',
      },
    },
    '& .MuiMenuItem-root': {
      fontSize: '12px',
      [theme.breakpoints.up(1520)]: {
        fontSize: '14px',
      },
    },
  };

  useEffect(() => {
    if (!modality && validModalities.length > 0) {
      setModality(validModalities[0]);
    }
  }, [modality, setModality]);

  // Handler for table header sorting
  const handleTableSort = (columnId: string, direction: 'asc' | 'desc') => {
    handleSortingChange([{ id: columnId, desc: direction === 'desc' }]);
  };

  // Handle table filter
  const handleTableFilter = (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => {
    // If all options are selected or none are selected, clear the filter
    const isAllSelected = allOptions && selectedValues.length === allOptions.length;

    // If no values selected or all options are selected, clear the filter
    if (!selectedValues.length || isAllSelected) {
      switch (columnId) {
        case 'service_provider_code': // Service provider column
          updateFilter('serviceProviderCodes', undefined);
          break;
        case 'dominant_modality':
          updateFilter('modalities', undefined);
          break;
        case 'shipper_code':
          updateFilter('shipperCodes', undefined);
          break;
        case 'consignee_code':
          updateFilter('consigneeCodes', undefined);
          break;
        case 'route':
          updateFilter('routes', undefined);
          break;
        case 'service_name': // Actual service name column
          updateFilter('services', undefined);
          break;
        case 'type':
          updateFilter('types', undefined);
          break;
        case 'carrier':
          updateFilter('carriers', undefined);
          break;
        case 'billed_entity':
          updateFilter('billingPartyCodes', undefined);
          break;
        case 'incoterm':
          updateFilter('incoterms', undefined);
          break;
        default:
          break;
      }
      return;
    }

    // Apply the filter based on column ID
    switch (columnId) {
      case 'service_provider_code': // Service provider column
        updateFilter('serviceProviderCodes', selectedValues);
        break;
      case 'dominant_modality':
        updateFilter('modalities', selectedValues as any);
        break;
      case 'shipper_code':
        updateFilter('shipperCodes', selectedValues);
        break;
      case 'consignee_code':
        updateFilter('consigneeCodes', selectedValues);
        break;
      case 'route':
        updateFilter('routes', selectedValues);
        break;
      case 'service_name': // Actual service name column
        updateFilter('services', selectedValues);
        break;
      case 'type':
        updateFilter('types', selectedValues);
        break;
      case 'carrier':
        updateFilter('carriers', selectedValues);
        break;
      case 'billed_entity':
        updateFilter('billingPartyCodes', selectedValues);
        break;
      case 'incoterm':
        updateFilter('incoterms', selectedValues as any);
        break;
      default:
        break;
    }
  };

  // Helper function to convert metadata objects to TableFilterItem array
  const createFilterItems = (data: Record<string, number> | undefined, columnKey?: string) => {
    if (!data) return [];

    // Entity columns that need beautifyEnum + uppercase
    const entityColumns = ['billedEntity', 'shipper', 'consignee', 'serviceProvider'];
    const shouldBeautify = columnKey && entityColumns.includes(columnKey);

    return Object.entries(data)
      .map(([key, count]) => {
        // Handle null values properly - keep null as null, not as string "null"
        let label = key === 'null' ? null : key;

        // Apply beautifyEnum + uppercase for entity columns
        if (shouldBeautify && label !== null) {
          label = beautifyEnum(String(label)).toUpperCase();
        }

        return {
          id: key === 'null' ? null : key,
          label,
          count: count as number,
        };
      })
      .sort((a, b) => {
        // Handle null values in sorting (null comes first)
        if (a.label === null) return -1;
        if (b.label === null) return 1;
        return a.label.localeCompare(b.label);
      });
  };

  const columns = useMemo(
    (): MRT_ColumnDef<IShippingServiceDTO>[] => [
      {
        enableEditing: false,
        enableSorting: false,
        accessorKey: 'no',
        header: 'No.',
      },
      {
        accessorKey: 'service_provider_code',
        header: 'SERVICE PROVIDER',
        enableSorting: false,
        Cell: ({ row }) => (row.original.service_provider_code ? beautifyEnum(String(row.original.service_provider_code)).toUpperCase() : '-'),
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.serviceProviderCodes, 'serviceProvider');
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='service_provider_code'
              filterOptions={filterItems}
              currentFilters={filters?.serviceProviderCodes || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems);
              }}
            />
          );
        },
        editVariant: 'select',
        editSelectOptions:
          currentServiceProviders?.map((provider) => ({
            value: provider.code,
            label: provider.code,
          })) || [],
      },
      {
        accessorKey: 'dominant_modality',
        header: 'MODALITY',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.modalities)?.map((item) => ({
            ...item,
            label: beautifyEnum(item.label),
          }));
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='dominant_modality'
              filterOptions={filterItems}
              currentFilters={filters?.modalities || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems);
              }}
            />
          );
        },
        editVariant: 'select',
        editSelectOptions: [
          {
            value: 'AIR',
            label: 'AIR',
          },
          {
            value: 'ROAD',
            label: 'ROAD',
          },
          {
            value: 'SEA',
            label: 'SEA',
          },
          {
            value: 'CT',
            label: 'CT',
          },
          {
            value: 'COURIER',
            label: 'COURIER',
          },
        ],
        muiEditTextFieldProps: {
          defaultValue: modality?.toUpperCase() || '',
        },
      },
      {
        accessorKey: 'shipper_code',
        header: 'SHIPPER',
        enableSorting: false,
        Cell: ({ row }) => row.original.shipper_code || '-',
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.shipperCodes);
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='shipper_code'
              filterOptions={filterItems}
              currentFilters={filters?.shipperCodes || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems);
              }}
            />
          );
        },
        Edit: ({ column, row, table }) => {
          const { creatingRow, editingRow } = table.getState();
          const isCreating = creatingRow?.id === row.id;
          const isEditing = editingRow?.id === row.id;

          return (
            <ShipperSelector
              // eslint-disable-next-line no-underscore-dangle
              value={(row._valuesCache[column.id] as string) ?? row.original.shipper_code}
              onChange={(newValue) => {
                // eslint-disable-next-line no-underscore-dangle
                row._valuesCache[column.id] = newValue;
                if (isCreating) {
                  table.setCreatingRow(row);
                } else if (isEditing) {
                  table.setEditingRow(row);
                }
              }}
            />
          );
        },
      },
      {
        accessorKey: 'consignee_code',
        header: 'CONSIGNEE',
        enableSorting: false,
        Cell: ({ row }) => row.original.consignee_code || '-',
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.consigneeCodes, 'consignee');
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='consignee_code'
              filterOptions={filterItems}
              currentFilters={filters?.consigneeCodes || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems);
              }}
            />
          );
        },
        Edit: ({ column, row, table }) => {
          const { creatingRow, editingRow } = table.getState();
          const isCreating = creatingRow?.id === row.id;
          const isEditing = editingRow?.id === row.id;

          return (
            <ConsigneeSelector
              // eslint-disable-next-line no-underscore-dangle
              value={(row._valuesCache[column.id] as string) ?? row.original.consignee_code}
              onChange={(newValue) => {
                // eslint-disable-next-line no-underscore-dangle
                row._valuesCache[column.id] = newValue;
                if (isCreating) {
                  table.setCreatingRow(row);
                } else if (isEditing) {
                  table.setEditingRow(row);
                }
              }}
            />
          );
        },
      },
      {
        accessorKey: 'billed_entity',
        header: 'BILLED ENTITY',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.billingPartyCodes, 'billedEntity');
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='billed_entity'
              filterOptions={filterItems}
              currentFilters={filters?.billingPartyCodes || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems);
              }}
            />
          );
        },
        editVariant: 'select',
        // Use an object format for editSelectOptions that MRT expects
        editSelectOptions: billedEntities.map((entity) => ({
          value: entity.value,
          label: entity.label, // This is the proper key used by MRT v2
        })),
        Cell: ({ cell }) => {
          // Display the code (label) instead of the id (value)
          const billedEntity = billedEntities.find((entity) => entity.value === cell.getValue());
          return billedEntity?.label ? beautifyEnum(billedEntity.label).toUpperCase() : '-';
        },
      },

      {
        accessorKey: 'route',
        header: 'TRADE LANE',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.routes);
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='route'
              filterOptions={filterItems}
              currentFilters={filters?.routes || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems);
              }}
            />
          );
        },
        Cell: ({ row }) => (
          <div className='flex items-center gap-10 min-w-[140px]'>
            <div className='relative'>{row?.original?.route}</div>
            {row?.original?.listZone?.length > 0 && (
              <CustomTooltip
                title={
                  <div className='flex items-center flex-wrap -mx-8 w-[180px] max-h-[320px] overflow-auto'>
                    {row?.original?.listZone?.map((item) => (
                      <div key={`${item?.pol}_${item?.pod}`} className='w-1/2 px-8'>
                        <div className='py-8 border-b border-neutral-70 text-14 xl:text-16'>
                          {item?.pol} / {item?.pod}
                        </div>
                      </div>
                    ))}
                  </div>
                }
              >
                <img width={20} src={IconInfo} alt='' />
              </CustomTooltip>
            )}
          </div>
        ),
        Edit: ({ column, row, table }) => {
          const { creatingRow, editingRow } = table.getState();
          const isCreating = creatingRow?.id === row.id;
          const isEditing = editingRow?.id === row.id;

          return (
            <TradeLaneZoneSelector
              routes={routesRef.current}
              value={row.original.route}
              onChange={(value) => {
                // eslint-disable-next-line no-underscore-dangle
                row._valuesCache[column.id] = value;
                if (isCreating) {
                  table.setCreatingRow(row);
                } else if (isEditing) {
                  table.setEditingRow(row);
                }
              }}
            />
          );
        },
      },
      {
        accessorKey: 'service',
        header: 'SERVICE',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.services);
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='service'
              filterOptions={filterItems}
              currentFilters={filters?.services || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter('service_name', selectedValues, filterItems);
              }}
            />
          );
        },
        muiEditTextFieldProps: {
          ...getTextFieldProps('service'),
          sx: editFieldStyles,
        },
      },
      {
        accessorKey: 'type',
        header: 'TYPE',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.types);
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='type'
              filterOptions={filterItems}
              currentFilters={filters?.types || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems);
              }}
            />
          );
        },
        muiEditTextFieldProps: {
          ...getTextFieldProps('type'),
          sx: editFieldStyles,
        },
      },
      {
        accessorKey: 'carrier',
        header: 'CARRIER',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.carriers);
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='carrier'
              filterOptions={filterItems}
              currentFilters={filters?.carriers || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems);
              }}
            />
          );
        },
        muiEditTextFieldProps: {
          ...getTextFieldProps('carrier'),
          sx: editFieldStyles,
        },
      },
      {
        accessorKey: 'incoterm',
        header: 'INCOTERM',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.incoterms);
          return (
            <ContractManagerColumnHeader
              column={column}
              accessorKey='incoterm'
              filterOptions={filterItems}
              currentFilters={filters?.incoterms || []}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems);
              }}
            />
          );
        },
        muiEditTextFieldProps: {
          select: true,
          ...getTextFieldProps('incoterm'),
          sx: editFieldStyles,
        },
      },
      {
        accessorKey: 'total_lead_time',
        header: 'TOTAL LEAD TIME',
        enableSorting: false,
        muiEditTextFieldProps: {
          ...getTextFieldProps('total_lead_time'),
          sx: editFieldStyles,
        },
      },
      {
        accessorKey: 'created_on',
        header: 'CREATED ON',
        enableSorting: false,
        enableEditing: false,
      },
    ],
    [validationErrors, metadataData?.data, billedEntities, filters]
  );

  const shippingServices = useMemo(() => {
    if (data?.data && Array.isArray(data?.data?.content) && data?.data?.content?.length > 0) {
      return data?.data?.content?.map((item, index) => ({
        no: index + 1,
        id: item?.id,
        service_provider_id: item?.serviceProviderId,
        service_provider_code: dataAllProviders?.data?.find((provider) => provider.id === item?.serviceProviderId)?.code || '-',
        shipper_code: item?.shipperInfo?.code || '-',
        shipper_name: item?.shipperInfo?.name || '-',
        consignee_code: item?.consigneeInfo?.code || '-',
        consignee_name: item?.consigneeInfo?.name || '-',
        dominant_modality: item?.dominantModality || '-',
        listZone: item?.listZone || [],
        // pol: item?.pol || '-',
        // pod: item?.pod || '-',
        route: item?.route,
        service: item?.service || '-',
        type: item?.type || '-',
        carrier: item?.carrier || '-',
        incoterm: item?.incoterm || '-',
        total_lead_time: item?.totalLeadTime || '-',
        active: item?.active ? 'True' : 'False',
        created_on: newFormatDateTimeString(item?.createdOn),
        billed_entity: billedEntities.find((entity) => entity.label === item?.billingPartyInfo?.code)?.value || null,
      }));
    }
    return [];
  }, [data, dataAllProviders, billedEntities]);

  const { mutateAsync: createShippingService } = useCreateShippingService();
  const { mutateAsync: updateShippingService } = useUpdateShippingService();
  const { mutateAsync: deleteShippingService } = useDeleteShippingService();

  // CREATE action
  const handleCreateShippingService: MRT_TableOptions<IShippingServiceDTO>['onCreatingRowSave'] = async ({ values, table }) => {
    // Validate the values before saving
    // const validationResult = validateItem(values);
    // if (!validationResult.isValid) {
    //   return; // Stop if validation fails
    // }

    normalizeDashValues(values);

    setIsSaving(true);
    try {
      const reqBody = buildShippingServicePayload(values, currentServiceProviders);

      await createShippingService(reqBody);

      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Shipping service created!',
      });
    } catch (error) {
      openToastError(new Error('Shipping service creation failed'));
    } finally {
      setIsSaving(false);
      clearValidationErrors();
      table.setCreatingRow(null);
    }
  };

  // UPDATE action
  const handleSaveShippingService: MRT_TableOptions<IShippingServiceDTO>['onEditingRowSave'] = async ({ row, values, table }) => {
    // Validate the values before saving
    // const validationResult = validateItem(values);
    // if (!validationResult.isValid) {
    //   return; // Stop if validation fails
    // }

    normalizeDashValues(values);

    setIsSaving(true);
    try {
      const reqBody = buildShippingServicePayload(values, currentServiceProviders);

      await updateShippingService({
        id: row.original.id,
        data: reqBody,
      });

      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Shipping service updated!',
      });
    } catch (error) {
      openToastError(new Error('Shipping service update failed'));
    } finally {
      setIsSaving(false);
      clearValidationErrors();
      table.setEditingRow(null);
    }
  };

  // DELETE action
  const openDeleteConfirmModal = async (row: MRT_Row<IShippingServiceDTO>) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure you want to delete this shipping service?')) {
      try {
        await deleteShippingService(row.original.id);
        openToast({
          toastType: 'success',
          title: 'Success',
          description: 'Shipping service deleted!',
        });
      } catch (error) {
        openToastError(new Error('Shipping service delete failed'));
      }
    }
  };

  const table = useMaterialReactTable({
    enableSorting: true,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: true,
    manualPagination: true,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    enableStickyHeader: true,
    muiTableContainerProps: { sx: { height: 'calc(100% - 56px)' } },
    muiTablePaperProps: { sx: { height: '100%' } },
    enableEditing: isAdmin,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    onCreatingRowCancel: () => clearValidationErrors(),
    onCreatingRowSave: handleCreateShippingService,
    onEditingRowCancel: () => clearValidationErrors(),
    onEditingRowSave: handleSaveShippingService,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableColumnPinning: true,
    positionActionsColumn: 'first',
    initialState: {
      columnPinning: { left: ['mrt-row-actions'] },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 120,
      },
    },

    muiTableHeadProps: {
      sx: {
        '& th:first-child': firstCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:first-child': firstCellStyles,
      },
    },

    // Remove box-shadow from action column and ensure styling consistency

    data: shippingServices as IShippingServiceDTO[],
    columns,
    state: {
      sorting,
      pagination,
      isSaving,
    },
    renderRowActions: isAdmin
      ? ({ row, table }) => (
          <Box sx={{ display: 'flex' }}>
            <Tooltip title='Edit'>
              <IconButton
                color='info'
                onClick={(e) => {
                  e.stopPropagation();
                  table.setEditingRow(row);
                }}
              >
                <img src={IconNewEdit} alt='Edit' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete'>
              <IconButton
                color='error'
                onClick={(e) => {
                  e.stopPropagation();
                  openDeleteConfirmModal(row);
                }}
              >
                <IconTrash />
              </IconButton>
            </Tooltip>
          </Box>
        )
      : undefined,
    muiTableBodyRowProps: ({ row, table }) => ({
      id: row.index === 0 ? tour.registerStep('chargeDetailsSheet', [], shippingServices.length > 0) : undefined,
      selected: row.original.id === shippingServiceId,
      onClick: () => {
        // If the table is currently editing a row, ignore row click to prevent drawer opening
        if (table.getState().editingRow) return;
        setShippingServiceId(row.original.id);
        setContract(null);

        // Set a short timeout to ensure the ref is attached before scrolling
        setTimeout(() => {
          if (selectedRowRef.current) {
            // Scroll the selected row into view positioning it in the center of the visible area
            selectedRowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }, 50);
      },
      ref: row.original.id === shippingServiceId ? selectedRowRef : undefined,
      // Add data attribute to help with auto-scrolling the selected row into view
      'data-row-selected': row.original.id === shippingServiceId ? 'true' : 'false',
      sx: {
        cursor: 'pointer',
        '&.Mui-selected .MuiTableCell-root': {
          backgroundColor: 'rgb(242, 242, 242)!important',
        },
        '&:hover': {
          backgroundColor: 'rgb(242, 242, 242)!important',
        },
        '&.Mui-selected:hover .MuiTableCell-root': {
          backgroundColor: 'rgb(242, 242, 242)!important',
        },
      },
    }),
  });

  // useEffect(() => {
  //   if (!shippingServiceId) {
  //     if (getShippingServiceStatus === 'success' && data.data.length > 0) {
  //       setShippingServiceId(data.data[0].id);
  //     } else {
  //       setShippingServiceId('');
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [getShippingServiceStatus, serviceProviderId, shippingServiceId, setShippingServiceId]);

  // Scroll to the selected row when shipping_service parameter is present and data is loaded
  useEffect(() => {
    const serviceParam = searchParams.get('shipping_service');
    if (serviceParam && serviceParam === shippingServiceId && selectedRowRef.current && getShippingServiceStatus === 'success') {
      // Add a small delay to ensure the component is fully rendered
      setTimeout(() => {
        selectedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [searchParams, shippingServiceId, getShippingServiceStatus]);

  // Remove the sync effect - it seems to be causing more problems than solving
  // Instead, let's make the handlers more direct

  // If user doesn't have access, show forbidden message (access check done at top after hooks)
  if (!hasPageAccess) {
    return (
      <div className='flex flex-col h-full items-center justify-center'>
        <h2 className='text-24 font-bold text-neutral-60 mb-8'>Access Denied</h2>
        <p className='text-16 text-neutral-50'>You don&apos;t have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      <div className='flex items-center gap-16 xl:gap-24 py-20 xl:py-24 px-16 xl:px-32 bg-white shadow-[0_6px_20px_0_rgba(0,0,0,0.04)] shrink-0'>
        <div className='flex items-center gap-18'>
          <button
            onClick={() => {
              // Get the stored section from localStorage or default to RATECARD
              const sectionName = localStorage.getItem('lastSectionName') || 'RATECARD';
              navigate(`/?section=${sectionName}`);
            }}
            className='w-40 h-40 rounded-full bg-neutral-10 border border-neutral-20 transition-all duration-300 hover:border-neutral-40 flex items-center justify-center cursor-pointer'
          >
            <img src={IconArrow} alt='Back' />
          </button>
          <div className='flex flex-col'>
            <h2 className='text-18 xl:text-24 font-bold text-blue_grey-90'>Rate Card Management</h2>
            <p className='text-12 xl:text-14 text-neutral-60'>Shipping Services & Charges</p>
          </div>
        </div>

        {isAdmin && (
          <div className='ml-auto'>
            <Button
              variant='primary'
              className='!pr-12 !pl-16 !ml-12'
              onClick={() => {
                try {
                  // Add a tiny delay to ensure DOM is ready
                  setTimeout(() => {
                    table.setCreatingRow(true);
                  }, 0);
                } catch (error) {
                  console.warn('Error creating new row:', error);
                  // Fallback method if the normal approach fails
                }
              }}
            >
              <div className='flex items-center gap-10'>
                <span className='text-14 xl:text-16'>Add New</span>
                <AddIcon />
              </div>
            </Button>
          </div>
        )}
      </div>

      <div className='flex flex-col flex-1 overflow-hidden'>
        {isLoading ? (
          <div className='flex-1 bg-white border-t border-neutral-20 overflow-hidden flex items-center justify-center'>
            <CircularProgress color='primary' />
          </div>
        ) : (
          <>
            <div
              id={tour.registerStep('shippingServicesTable', [], shippingServices.length > 0)}
              className='flex-1 bg-white border-t border-neutral-20 overflow-auto'
            >
              {/* Loading state */}
              {getShippingServiceStatus === 'loading' && (
                <div className='h-full flex items-center justify-center'>
                  <CircularProgress color='primary' />
                </div>
              )}

              {/* Empty state */}
              {getShippingServiceStatus !== 'loading' && shippingServices.length === 0 && (
                <div className='h-full flex items-center justify-center'>
                  <p className='text-neutral-60'>No records to display</p>
                </div>
              )}

              {/* Data state */}
              {getShippingServiceStatus !== 'loading' && shippingServices.length > 0 && (
                <TableMain
                  table={table}
                  tableContainerProps={{
                    sx: {
                      height: '100%',
                      maxHeight: '100%',
                      overflow: 'auto',
                      '& .MuiTable-root': {
                        borderCollapse: 'separate',
                      },
                    },
                  }}
                />
              )}
            </div>
            <div
              id={tour.registerStep('paginationShippingServices', [], shippingServices.length > 0)}
              className='absolute z-10 bottom-0 p-12 xl:p-16 bg-neutral-10 flex items-center justify-between border-t border-neutral-20 shrink-0 w-full min-h-[70px] shipping-service-pagination'
            >
              <UnifiedTablePagination
                pageIndex={pagination.pageIndex}
                pageSize={pagination.pageSize}
                totalItems={totalItemsRef.current || data?.data?.totalElements || 0}
                onPageChange={onChangePage}
                onPageSizeChange={onChangePageSize}
                pageSizeOptions={[100, 500, 1000]}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
